import React from 'react';
import styles from './SortIcon.module.scss';
import { SortConfig } from '../../../../../../../../utils/constants/commonConstants';
import Tooltip from '../../../../../../../../common/_custom/Tooltip/Tooltip';

// Import SVG files from assets folder
import SwapVertIcon from '../../../../../../../../assets/swap_vert.svg';
import SwapVertUpIcon from '../../../../../../../../assets/swap_vert_up.svg';
import SwapVertDownIcon from '../../../../../../../../assets/swap_vert_down.svg';

interface SortIconProps {
    field: string;
    sortConfig?: SortConfig;
    onSort?: (field: string) => void;
    ascToolTipText?: string;
    descToolTipText?: string;
    alwaysVisible?: boolean;
    className?: string;
}

const SortIcon: React.FC<SortIconProps> = ({ field, sortConfig, onSort, ascToolTipText, descToolTipText, alwaysVisible, className }) => {
    const handleClick = (e: React.SyntheticEvent) => {
        e.stopPropagation();
        if (onSort) {
            onSort(field);
        }
    };

    const isActive = sortConfig?.field === field;
    const direction = isActive ? sortConfig.direction : null;

    // Normalize direction to handle both uppercase and lowercase
    const normalizedDirection = direction?.toLowerCase() as 'asc' | 'desc' | null;

    /* Keyboard parity with the click: the control is a div, so without this it is unreachable and
       inoperable for keyboard users. */
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick(e);
        }
    };

    const sortStateLabel =
        normalizedDirection === 'asc' ? 'sorted ascending' : normalizedDirection === 'desc' ? 'sorted descending' : 'not sorted';

    const getIcon = () => {
        if (normalizedDirection === 'asc') {
            const icon = <img src={SwapVertUpIcon} alt="Sort ascending" />;

            // Show tooltip only if ascToolTipText is provided
            if (ascToolTipText) {
                return (
                    <Tooltip
                        content="Ascending Sort"
                        direction="bottom"
                        small
                        style={{
                            zIndex: 10000,
                            position: 'absolute',
                            pointerEvents: 'none',
                            minWidth: '100px',
                            maxWidth: '150px',
                        }}
                    >
                        {icon}
                    </Tooltip>
                );
            }
            return icon;
        } else if (normalizedDirection === 'desc') {
            const icon = <img src={SwapVertDownIcon} alt="Sort descending" />;

            // Show tooltip only if descToolTipText is provided
            if (descToolTipText) {
                return (
                    <Tooltip
                        content="Descending Sort"
                        direction="bottom"
                        small
                        style={{
                            zIndex: 10000,
                            position: 'absolute',
                            pointerEvents: 'none',
                            minWidth: '100px',
                            maxWidth: '150px',
                        }}
                    >
                        {icon}
                    </Tooltip>
                );
            }
            return icon;
        } else {
            // No tooltip for the base/neutral icon
            return <img src={SwapVertIcon} alt="Sort" />;
        }
    };

    return (
        <div
            className={`${styles.SortIcon} ${normalizedDirection ? styles.SortIconActive : alwaysVisible ? styles.SortIconHovered : ''} ${
                className ?? ''
            }`}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            role="button"
            tabIndex={0}
            aria-label={`Sort by ${field}, ${sortStateLabel}`}
        >
            <div className={`${styles.SortImage} ${normalizedDirection ? styles.Active + ' sort-icon-active' : ''}`}>{getIcon()}</div>
        </div>
    );
};

export default SortIcon;
